import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getDataLayerUtility } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import CENCustonWrenchIcon from "@tCentro/Icons/CENCustonWrenchIcon";
import ProductListActions from "@tCentro/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@tCentro/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@tCentro/Overrides/Widgets/ProductList/ProductListProductInformation";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import CENProductListProductCompare from "@tCentro/Widgets/Product/CENProductListProductCompare";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const productsDataView = getProductListDataView(state);

    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        path: getSelectedCategoryPath(state) || getLocation(state).pathname,
        originalQuery: productsDataView.value ? productsDataView.originalQuery : undefined,
    };
};

type Props = ProductCardSelections & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    compareWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    imageProductWrapper?: InjectableCss;
    moreOptionsStyles?: InjectableCss;
    wrenchIconStyles?: IconPresentationProps;
    configureProductPlpStyles?: InjectableCss;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    moreOptionsStyles: {
        css: css`
            margin-bottom: 8px;
            border: 2px solid ${getColor("primary")};
            padding: 4px 12px 4px 8px;
            border-radius: 16px;
            span {
                font-family: Poppins;
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
                color: ${getColor("primary")} !important;
                display: flex;
                align-items: center;
                gap: 2px;
            }
        `,
    },
    configureProductPlpStyles: {
        css: css`
            margin-top: 52px;
            margin-bottom: 12px;
            font-family: Poppins;
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        margin-top: 60px;
                        margin-bottom: 24px;
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 992px) and (max-width: 1181px) {
                margin-top: 60px !important;
                margin-bottom: 24px !important;
            }
            @media (min-width: 1517px) {
                margin-top: 60px !important;
                margin-bottom: 24px !important;
            }
        `,
    },
    wrenchIconStyles: {
        size: 16,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            background: ${COLORS.background.grayShade};
            padding: 24px 10px;
            position: relative;
            padding-bottom: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 50px;
                    `,
                    css`
                        padding-bottom: 50px;
                    `,
                    css`
                        padding-bottom: 70px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    compareWrapper: {
        css: css`
            div[data-test-selector^="productListProductCompare"] {
                width: 100%;
                div {
                    &:last-child {
                        justify-content: flex-start;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                null,
                                null,
                                css`
                                    justify-content: flex-end;
                                `,
                                css`
                                    justify-content: flex-end;
                                `,
                                css`
                                    justify-content: flex-end;
                                `,
                            ])}
                    }
                }
            }
        `,
    },
    imageProductWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    breadcrumbLinks,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    productInfo,
    path,
    originalQuery
}) => {

    const onClickHandler = (event: React.MouseEvent) => {
        getDataLayerUtility({
            product,
            pricing: productInfo.pricing,
            quantity: productInfo.qtyOrdered || 1,
            breadcrumbs: breadcrumbLinks,
            originalQuery,
        });
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            <StyledWrapper {...styles.compareWrapper}>
                <CENProductListProductCompare showCompare={showCompare} showAddToList={showAddToList} />
            </StyledWrapper>
            <StyledWrapper {...styles.imageProductWrapper}>
                <StyledWrapper>
                    {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                    <ProductListProductImage
                        showImage={showImage}
                        showCompare={showCompare}
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                        onClick={onClickHandler}
                    />
                </StyledWrapper>
                <Hidden
                    below="md"
                    css={css`
                        display: flex;
                        justify-content: flex-start;
                    `}
                >
                    {product?.isVariantParent && (
                        <Link href={product?.canonicalUrl} {...styles.moreOptionsStyles}>
                            <Icon src={CENCustonWrenchIcon} {...styles.wrenchIconStyles} />
                            {translate("Configure Product")}
                        </Link>
                    )}
                </Hidden>
                <StyledWrapper>
                    <ProductListProductInformation
                        showAttributes={showAttributes}
                        showBrand={showBrand}
                        showPartNumbers={showPartNumbers}
                        showTitle={showTitle}
                        showImageBadges={showImageBadges}
                        maxImageBadges={maximumNumberOfImageBadges}
                        showTextBadges={showTextBadges}
                        maxTextBadges={maximumNumberOfTextBadges}
                        onClick={onClickHandler}
                    />
                </StyledWrapper>
                <Hidden below="md">
                    {product?.isVariantParent && (
                        <Typography as="p" {...styles.configureProductPlpStyles}>
                            {siteMessage("configureProduct_Plp_Msg")}
                        </Typography>
                    )}
                </Hidden>
                {/* <Hidden above="sm" css={css``}>
                    {product?.isVariantParent && (
                        <Typography as="p" {...styles.configureProductPlpStyles}>
                            {siteMessage("configureProduct_Plp_Msg")}
                        </Typography>
                    )}
                </Hidden> */}
            </StyledWrapper>
            <StyledWrapper>
                <ProductListActions showPrice={showPrice} showAvailability={showAvailability} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductGridCard));
